/**
 * WeaveIn.ai Platform - Family Members Tab Component
 * Manages family unit members (spouse, children, dependents) for a member
 */

import React, { useState, useEffect, useCallback } from 'react';
import { apiClient } from '@/services/apiClient';
import { useAppTranslation } from '@/contexts/TranslationContext';
import { useFormattedDate } from '../../hooks/useUserTimezone';
import { ProfileField, CustomField, membersAPI } from '@/services/api/members';
import RecordFieldGroupsSection from '@/components/records/RecordFieldGroupsSection';
import PhoneInput from '@/components/ui/PhoneInput';
import { computeAge } from '@/utils/dateTime';
import { evaluateVisibilityOperator } from '../forms/fieldVisibility';

// ============================================================================
// Types
// ============================================================================

interface FamilyMember {
  id: number;
  member_id: number;
  relationship: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  ssn_last4?: string;
  gender?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  coverage_type: string;
  coverage_start_date?: string;
  coverage_end_date?: string;
  is_primary_beneficiary: boolean;
  status: string;
  verification_status: string;
  verified_at?: string;
  verified_by?: number;
  notes?: string;
  // TRUS-353: per-CBO configurable custom/reused field values keyed by field name.
  custom_fields?: Record<string, unknown>;
  created_at: string;
  updated_at?: string;
}

interface FamilyMemberCreate {
  relationship: string;
  first_name: string;
  last_name: string;
  date_of_birth?: string;
  ssn_last4?: string;
  gender?: string;
  email?: string;
  phone?: string;
  address_line1?: string;
  address_line2?: string;
  city?: string;
  state?: string;
  zip_code?: string;
  coverage_type?: string;
  coverage_start_date?: string;
  coverage_end_date?: string;
  is_primary_beneficiary?: boolean;
  notes?: string;
  custom_fields?: Record<string, unknown>;
}

interface FamilyMembersTabProps {
  memberId: number;
  isReadOnly?: boolean;
}

// ============================================================================
// Constants
// ============================================================================

const RELATIONSHIP_TYPES = [
  { value: 'SPOUSE', label: 'Spouse' },
  { value: 'CHILD', label: 'Child' },
  { value: 'PARENT', label: 'Parent' },
  { value: 'SIBLING', label: 'Sibling' },
  { value: 'GRANDPARENT', label: 'Grandparent' },
  { value: 'GRANDCHILD', label: 'Grandchild' },
  { value: 'DOMESTIC_PARTNER', label: 'Domestic Partner' },
  { value: 'LEGAL_GUARDIAN', label: 'Legal Guardian' },
  { value: 'WARD', label: 'Ward' },
  { value: 'OTHER', label: 'Other' },
];

const COVERAGE_TYPES = [
  { value: 'MEDICAL', label: 'Medical' },
  { value: 'DENTAL', label: 'Dental' },
  { value: 'VISION', label: 'Vision' },
  { value: 'FULL', label: 'Full Coverage' },
  { value: 'NONE', label: 'No Coverage' },
];

const GENDER_OPTIONS = [
  { value: 'male', label: 'Male' },
  { value: 'female', label: 'Female' },
  { value: 'non_binary', label: 'Non-Binary' },
  { value: 'prefer_not_to_say', label: 'Prefer Not to Say' },
];

const STATUS_OPTIONS = [
  { value: 'ACTIVE', label: 'Active' },
  { value: 'INACTIVE', label: 'Inactive' },
  { value: 'PENDING', label: 'Pending' },
  { value: 'DECEASED', label: 'Deceased' },
];

// ============================================================================
// Component
// ============================================================================

export default function FamilyMembersTab({ memberId, isReadOnly = false }: FamilyMembersTabProps) {
  const { t } = useAppTranslation();
  const { fmt } = useFormattedDate();
  const [familyMembers, setFamilyMembers] = useState<FamilyMember[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [showAddForm, setShowAddForm] = useState(false);
  const [editingMember, setEditingMember] = useState<FamilyMember | null>(null);
  const [formData, setFormData] = useState<FamilyMemberCreate>({
    relationship: 'SPOUSE',
    first_name: '',
    last_name: '',
    coverage_type: 'FULL',
    custom_fields: {},
  });
  const [saving, setSaving] = useState(false);

  // Field-visibility config (Field Config Manager "Hide field" toggles). Family
  // member fields are core fields keyed fm_* (e.g. fm_ssn_last4). Without this
  // the SSN and coverage inputs rendered regardless of an admin hiding them.
  // record_type MUST be 'family_member': /profile-fields filters by
  // applies_to_record_types and defaults to 'member', which returns zero fm_*
  // fields — so isFmFieldHidden() never found a config row and every hide was a
  // silent no-op (TRUS-574). The custom-field load below already passes it.
  const [profileFields, setProfileFields] = useState<ProfileField[]>([]);
  useEffect(() => {
    membersAPI.getProfileFields({ include_hidden: true, record_type: 'family_member' })
      .then(setProfileFields)
      .catch(() => setProfileFields([]));
  }, []);
  const isFmFieldHidden = (fieldKey: string): boolean => {
    const config = profileFields.find(f => f.is_core_field && f.field_key === fieldKey);
    return config?.is_hidden ?? false;
  };

  // TRUS-353: custom/reused fields an admin scoped to the Family Members section
  // (Member Field Config → "Family Members"). Rendered in the add/edit form and
  // on each card; values are stored per family member.
  const [customFieldDefs, setCustomFieldDefs] = useState<CustomField[]>([]);
  useEffect(() => {
    // TRUS-401: `/custom-fields` returns fields ordered `category, friendly_name`
    // — alphabetically. The order an admin arranges in Field Configuration lives
    // in member_field_configurations and is served by the unified
    // configuration endpoint, so the two are fetched together and the field
    // definitions are sorted into the configured order. The definitions
    // themselves still come from /custom-fields because this section renders
    // from the com_fields shape (id/name/field_options/str_len); the unified
    // endpoint supplies ORDER and the admin's display name.
    Promise.all([
      membersAPI.listCustomFields({ record_type: 'family_member', include_hidden: false }),
      membersAPI
        .getProfileFields({ record_type: 'family_member', include_hidden: false })
        .catch(() => [] as ProfileField[]),
    ])
      .then(([defs, configured]) => {
        const orderByKey = new Map<string, number>();
        const labelByKey = new Map<string, string>();
        (configured || []).forEach((c, i) => {
          if (c.is_core_field) return; // standard fields render natively above
          orderByKey.set(c.field_key, i);
          if (c.display_name) labelByKey.set(c.field_key, c.display_name);
        });
        const ordered = [...(defs || [])].sort((a, b) => {
          // A field with no configuration row sorts after the configured ones,
          // keeping its existing relative order rather than jumping to the top.
          const ai = orderByKey.has(a.name) ? (orderByKey.get(a.name) as number) : Number.MAX_SAFE_INTEGER;
          const bi = orderByKey.has(b.name) ? (orderByKey.get(b.name) as number) : Number.MAX_SAFE_INTEGER;
          if (ai !== bi) return ai - bi;
          return (a.friendly_name || a.name).localeCompare(b.friendly_name || b.name);
        });
        setCustomFieldDefs(
          ordered.map((f) =>
            labelByKey.has(f.name) ? { ...f, friendly_name: labelByKey.get(f.name) as string } : f
          )
        );
      })
      .catch(() => setCustomFieldDefs([]));
  }, []);
  const setCustomField = (name: string, value: unknown) => {
    setFormData(prev => ({ ...prev, custom_fields: { ...(prev.custom_fields || {}), [name]: value } }));
  };
  const customFieldLabel = (f: CustomField) => f.friendly_name || f.name;

  // TRUS-400: honor a field's conditional (parent/child) visibility_rules the
  // same way the member profile and form renderers do. Conditions reference the
  // parent field by its `name` and the value bags are keyed by field name, so a
  // direct lookup resolves the dependency. `bag` is the in-progress form values
  // (add/edit) or a saved member's custom_fields (card view). Without this,
  // every conditional child rendered unconditionally on the Family Member form.
  const isCustomFieldVisible = (
    f: CustomField,
    bag?: Record<string, unknown>
  ): boolean => {
    const rules = f.visibility_rules;
    if (!rules || !rules.conditions || rules.conditions.length === 0) return true;
    const results = rules.conditions.map((cond) =>
      evaluateVisibilityOperator(cond.operator, bag?.[cond.field], cond.value)
    );
    return rules.logic === 'OR' ? results.some(Boolean) : results.every(Boolean);
  };

  // Load family members
  const loadFamilyMembers = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);
      const response = await apiClient.get(`/api/members/${memberId}/family`);
      setFamilyMembers(response.data);
    } catch (err: any) {
      console.error('Failed to load family members:', err);
      setError(err.response?.data?.detail || 'Failed to load family members');
    } finally {
      setLoading(false);
    }
  }, [memberId]);

  useEffect(() => {
    loadFamilyMembers();
  }, [loadFamilyMembers]);

  // Reset form
  const resetForm = () => {
    setFormData({
      relationship: 'SPOUSE',
      first_name: '',
      last_name: '',
      coverage_type: 'FULL',
      custom_fields: {},
    });
    setEditingMember(null);
    setShowAddForm(false);
  };

  // Handle form submit
  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setSaving(true);

    try {
      if (editingMember) {
        await apiClient.put(`/api/members/${memberId}/family/${editingMember.id}`, formData);
      } else {
        await apiClient.post(`/api/members/${memberId}/family`, formData);
      }
      resetForm();
      loadFamilyMembers();
    } catch (err: any) {
      console.error('Failed to save family member:', err);
      setError(err.response?.data?.detail || 'Failed to save family member');
    } finally {
      setSaving(false);
    }
  };

  // Handle delete
  const handleDelete = async (familyMemberId: number) => {
    if (!window.confirm('Are you sure you want to remove this family member?')) {
      return;
    }

    try {
      await apiClient.delete(`/api/members/${memberId}/family/${familyMemberId}`);
      loadFamilyMembers();
    } catch (err: any) {
      console.error('Failed to delete family member:', err);
      setError(err.response?.data?.detail || 'Failed to delete family member');
    }
  };

  // Handle edit
  const handleEdit = (member: FamilyMember) => {
    setEditingMember(member);
    setFormData({
      relationship: member.relationship,
      first_name: member.first_name,
      last_name: member.last_name,
      date_of_birth: member.date_of_birth,
      ssn_last4: member.ssn_last4,
      gender: member.gender,
      email: member.email,
      phone: member.phone,
      address_line1: member.address_line1,
      address_line2: member.address_line2,
      city: member.city,
      state: member.state,
      zip_code: member.zip_code,
      coverage_type: member.coverage_type,
      coverage_start_date: member.coverage_start_date,
      coverage_end_date: member.coverage_end_date,
      is_primary_beneficiary: member.is_primary_beneficiary,
      notes: member.notes,
      custom_fields: (member.custom_fields as Record<string, unknown>) || {},
    });
    setShowAddForm(true);
  };

  // Handle verify
  const handleVerify = async (familyMemberId: number) => {
    try {
      await apiClient.post(`/api/members/${memberId}/family/${familyMemberId}/verify`);
      loadFamilyMembers();
    } catch (err: any) {
      console.error('Failed to verify family member:', err);
      setError(err.response?.data?.detail || 'Failed to verify family member');
    }
  };

  // Get relationship label
  const getRelationshipLabel = (value: string) => {
    return RELATIONSHIP_TYPES.find(r => r.value === value)?.label || value;
  };

  // Get coverage label
  const getCoverageLabel = (value: string) => {
    return COVERAGE_TYPES.find(c => c.value === value)?.label || value;
  };

  // Get status color
  const getStatusColor = (status: string) => {
    switch (status) {
      case 'ACTIVE':
        return 'bg-green-100 text-green-800 dark:bg-green-900 dark:text-green-200';
      case 'INACTIVE':
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
      case 'PENDING':
        return 'bg-yellow-100 text-yellow-800 dark:bg-yellow-900 dark:text-yellow-200';
      case 'DECEASED':
        return 'bg-red-100 text-red-800 dark:bg-red-900 dark:text-red-200';
      default:
        return 'bg-gray-100 text-gray-800 dark:bg-gray-700 dark:text-gray-200';
    }
  };

  // Loading state
  if (loading) {
    return (
      <div className="p-6 flex justify-center">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
      </div>
    );
  }

  return (
    <div className="p-6">
      {/* Header */}
      <div className="flex justify-between items-center mb-6">
        <h3 className="text-lg font-semibold text-gray-900 dark:text-white">
          {t('members.familyMembers', 'Family Members')}
          <span className="ml-2 text-sm font-normal text-gray-500">
            ({familyMembers.length} {familyMembers.length === 1 ? 'member' : 'members'})
          </span>
        </h3>
        {!isReadOnly && !showAddForm && (
          <button
            onClick={() => setShowAddForm(true)}
            className="px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 transition-colors"
          >
            + Add Family Member
          </button>
        )}
      </div>

      {/* Error message */}
      {error && (
        <div className="mb-4 p-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 rounded-lg text-red-700 dark:text-red-300">
          {error}
          <button onClick={() => setError(null)} className="ml-2 underline">Dismiss</button>
        </div>
      )}

      {/* Add/Edit Form */}
      {showAddForm && (
        <div className="mb-6 p-6 bg-gray-50 dark:bg-gray-800 rounded-lg border border-gray-200 dark:border-gray-700">
          <h4 className="text-md font-semibold mb-4 text-gray-900 dark:text-white">
            {editingMember ? 'Edit Family Member' : 'Add Family Member'}
          </h4>
          <form onSubmit={handleSubmit} className="space-y-4">
            {/* Row 1: Relationship, First Name, Last Name */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Relationship *
                </label>
                <select
                  value={formData.relationship}
                  onChange={(e) => setFormData({ ...formData, relationship: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                >
                  {RELATIONSHIP_TYPES.map(r => (
                    <option key={r.value} value={r.value}>{r.label}</option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  First Name *
                </label>
                <input
                  type="text"
                  value={formData.first_name}
                  onChange={(e) => setFormData({ ...formData, first_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Last Name *
                </label>
                <input
                  type="text"
                  value={formData.last_name}
                  onChange={(e) => setFormData({ ...formData, last_name: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  required
                />
              </div>
            </div>

            {/* Row 2: DOB, Gender, SSN Last 4 */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Date of Birth
                </label>
                <input
                  type="date"
                  value={formData.date_of_birth || ''}
                  onChange={(e) => setFormData({ ...formData, date_of_birth: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
                {formData.date_of_birth && (
                  <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                    Age: {computeAge(formData.date_of_birth)} yrs
                  </p>
                )}
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Gender
                </label>
                <select
                  value={formData.gender || ''}
                  onChange={(e) => setFormData({ ...formData, gender: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="">Select...</option>
                  {GENDER_OPTIONS.map(g => (
                    <option key={g.value} value={g.value}>{g.label}</option>
                  ))}
                </select>
              </div>
              {!isFmFieldHidden('fm_ssn_last4') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    SSN (Last 4)
                  </label>
                  <input
                    type="text"
                    value={formData.ssn_last4 || ''}
                    onChange={(e) => setFormData({ ...formData, ssn_last4: e.target.value.replace(/\D/g, '').slice(0, 4) })}
                    placeholder="XXXX"
                    maxLength={4}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}
            </div>

            {/* Row 3: Email, Phone */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Email
                </label>
                <input
                  type="email"
                  value={formData.email || ''}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                  Phone
                </label>
                <PhoneInput
                  value={formData.phone || ''}
                  onChange={(v) => setFormData({ ...formData, phone: v })}
                  className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                />
              </div>
            </div>

            {/* Row 4: Coverage Type, Coverage Dates */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
              {!isFmFieldHidden('fm_coverage_type') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Coverage Type
                  </label>
                  <select
                    value={formData.coverage_type || 'FULL'}
                    onChange={(e) => setFormData({ ...formData, coverage_type: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  >
                    {COVERAGE_TYPES.map(c => (
                      <option key={c.value} value={c.value}>{c.label}</option>
                    ))}
                  </select>
                </div>
              )}
              {!isFmFieldHidden('fm_coverage_start_date') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Coverage Start
                  </label>
                  <input
                    type="date"
                    value={formData.coverage_start_date || ''}
                    onChange={(e) => setFormData({ ...formData, coverage_start_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}
              {!isFmFieldHidden('fm_coverage_end_date') && (
                <div>
                  <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                    Coverage End
                  </label>
                  <input
                    type="date"
                    value={formData.coverage_end_date || ''}
                    onChange={(e) => setFormData({ ...formData, coverage_end_date: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                  />
                </div>
              )}
            </div>

            {/* Row 5: Primary Beneficiary checkbox */}
            {!isFmFieldHidden('fm_is_primary_beneficiary') && (
              <div className="flex items-center">
                <input
                  type="checkbox"
                  id="is_primary_beneficiary"
                  checked={formData.is_primary_beneficiary || false}
                  onChange={(e) => setFormData({ ...formData, is_primary_beneficiary: e.target.checked })}
                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                />
                <label htmlFor="is_primary_beneficiary" className="ml-2 text-sm text-gray-700 dark:text-gray-300">
                  Primary Beneficiary
                </label>
              </div>
            )}

            {/* Row 6: Notes */}
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Notes
              </label>
              <textarea
                value={formData.notes || ''}
                onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
                rows={2}
                className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
              />
            </div>

            {/* TRUS-353: admin-configured custom / reused fields for this section */}
            {customFieldDefs.length > 0 && (
              <div className="pt-2 border-t border-gray-200 dark:border-gray-700 grid grid-cols-1 md:grid-cols-2 gap-4">
                {customFieldDefs
                  .filter((f) => isCustomFieldVisible(f, formData.custom_fields))
                  .map((f) => {
                  const raw = formData.custom_fields?.[f.name];
                  const inputClass =
                    'w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white';
                  const isCheckboxLike = f.type === 'bool' || f.type === 'toggle' || f.type === 'checkbox';
                  return (
                    <div key={f.id} className={f.type === 'long_text' ? 'md:col-span-2' : undefined}>
                      {!isCheckboxLike && (
                        <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {customFieldLabel(f)}{f.mandatory ? ' *' : ''}
                        </label>
                      )}
                      {f.type === 'long_text' ? (
                        <textarea
                          value={raw != null ? String(raw) : ''}
                          onChange={(e) => setCustomField(f.name, e.target.value)}
                          rows={2}
                          required={f.mandatory}
                          className={inputClass}
                        />
                      ) : f.type === 'int' ? (
                        <input
                          type="number"
                          value={raw != null ? String(raw) : ''}
                          onChange={(e) => setCustomField(f.name, e.target.value)}
                          required={f.mandatory}
                          className={inputClass}
                        />
                      ) : f.type === 'date' || f.type === 'fixed_date' ? (
                        <input
                          type="date"
                          value={raw != null ? String(raw) : ''}
                          onChange={(e) => setCustomField(f.name, e.target.value)}
                          required={f.mandatory}
                          className={inputClass}
                        />
                      ) : f.type === 'datetime' ? (
                        <input
                          type="datetime-local"
                          value={raw != null ? String(raw) : ''}
                          onChange={(e) => setCustomField(f.name, e.target.value)}
                          required={f.mandatory}
                          className={inputClass}
                        />
                      ) : f.type === 'time' ? (
                        <input
                          type="time"
                          value={raw != null ? String(raw) : ''}
                          onChange={(e) => setCustomField(f.name, e.target.value)}
                          required={f.mandatory}
                          className={inputClass}
                        />
                      ) : f.type === 'select' || f.type === 'radio' ? (
                        <select
                          value={raw != null ? String(raw) : ''}
                          onChange={(e) => setCustomField(f.name, e.target.value)}
                          required={f.mandatory}
                          className={inputClass}
                        >
                          <option value="">Select...</option>
                          {(f.field_options || []).map((o) => (
                            <option key={o.value} value={o.value}>{o.label}</option>
                          ))}
                        </select>
                      ) : f.type === 'multi_select' ? (
                        <div className="flex flex-wrap gap-3 pt-1">
                          {(f.field_options || []).map((o) => {
                            const arr = Array.isArray(raw) ? (raw as string[]) : [];
                            const checked = arr.includes(o.value);
                            return (
                              <label key={o.value} className="inline-flex items-center gap-1.5 text-sm text-gray-700 dark:text-gray-300">
                                <input
                                  type="checkbox"
                                  checked={checked}
                                  onChange={(e) =>
                                    setCustomField(
                                      f.name,
                                      e.target.checked ? [...arr, o.value] : arr.filter((v) => v !== o.value)
                                    )
                                  }
                                  className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                                />
                                {o.label}
                              </label>
                            );
                          })}
                        </div>
                      ) : isCheckboxLike ? (
                        <label className="flex items-center gap-2 text-sm text-gray-700 dark:text-gray-300">
                          <input
                            type="checkbox"
                            checked={raw === true || raw === 'true'}
                            onChange={(e) => setCustomField(f.name, e.target.checked)}
                            className="h-4 w-4 text-blue-600 rounded border-gray-300 focus:ring-blue-500"
                          />
                          {customFieldLabel(f)}
                        </label>
                      ) : (
                        <input
                          type="text"
                          value={raw != null ? String(raw) : ''}
                          onChange={(e) => setCustomField(f.name, e.target.value)}
                          required={f.mandatory}
                          className={inputClass}
                        />
                      )}
                    </div>
                  );
                })}
              </div>
            )}

            {/* Form buttons */}
            <div className="flex gap-3">
              <button
                type="submit"
                disabled={saving}
                className="px-4 py-2 bg-v2accent text-white rounded-lg hover:bg-v2accent/90 disabled:opacity-50 transition-colors"
              >
                {saving ? 'Saving...' : editingMember ? 'Update' : 'Add Family Member'}
              </button>
              <button
                type="button"
                onClick={resetForm}
                className="px-4 py-2 bg-gray-200 dark:bg-gray-600 text-gray-800 dark:text-white rounded-lg hover:bg-gray-300 dark:hover:bg-gray-500 transition-colors"
              >
                Cancel
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Family Members List */}
      {familyMembers.length === 0 ? (
        <div className="text-center py-8 text-gray-500 dark:text-gray-400">
          <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M17 20h5v-2a3 3 0 00-5.356-1.857M17 20H7m10 0v-2c0-.656-.126-1.283-.356-1.857M7 20H2v-2a3 3 0 015.356-1.857M7 20v-2c0-.656.126-1.283.356-1.857m0 0a5.002 5.002 0 019.288 0M15 7a3 3 0 11-6 0 3 3 0 016 0zm6 3a2 2 0 11-4 0 2 2 0 014 0zM7 10a2 2 0 11-4 0 2 2 0 014 0z" />
          </svg>
          <p className="mt-2">No family members added yet</p>
          {!isReadOnly && (
            <button
              onClick={() => setShowAddForm(true)}
              className="mt-3 text-blue-600 hover:text-blue-700 dark:text-blue-400"
            >
              Add the first family member
            </button>
          )}
        </div>
      ) : (
        <div className="space-y-4">
          {familyMembers.map((member) => (
            <div
              key={member.id}
              className="p-4 bg-white dark:bg-gray-800 border border-gray-200 dark:border-gray-700 rounded-lg shadow-sm"
            >
              <div className="flex justify-between items-start">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <h4 className="text-md font-semibold text-gray-900 dark:text-white">
                      {member.first_name} {member.last_name}
                    </h4>
                    <span className="px-2 py-0.5 text-xs rounded-full bg-blue-100 text-blue-800 dark:bg-blue-900 dark:text-blue-200">
                      {getRelationshipLabel(member.relationship)}
                    </span>
                    <span className={`px-2 py-0.5 text-xs rounded-full ${getStatusColor(member.status)}`}>
                      {member.status}
                    </span>
                    {member.is_primary_beneficiary && (
                      <span className="px-2 py-0.5 text-xs rounded-full bg-purple-100 text-purple-800 dark:bg-purple-900 dark:text-purple-200">
                        Primary Beneficiary
                      </span>
                    )}
                    {member.verification_status === 'VERIFIED' && (
                      <span className="text-green-600 dark:text-green-400" title="Verified">
                        <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20">
                          <path fillRule="evenodd" d="M6.267 3.455a3.066 3.066 0 001.745-.723 3.066 3.066 0 013.976 0 3.066 3.066 0 001.745.723 3.066 3.066 0 012.812 2.812c.051.643.304 1.254.723 1.745a3.066 3.066 0 010 3.976 3.066 3.066 0 00-.723 1.745 3.066 3.066 0 01-2.812 2.812 3.066 3.066 0 00-1.745.723 3.066 3.066 0 01-3.976 0 3.066 3.066 0 00-1.745-.723 3.066 3.066 0 01-2.812-2.812 3.066 3.066 0 00-.723-1.745 3.066 3.066 0 010-3.976 3.066 3.066 0 00.723-1.745 3.066 3.066 0 012.812-2.812zm7.44 5.252a1 1 0 00-1.414-1.414L9 10.586 7.707 9.293a1 1 0 00-1.414 1.414l2 2a1 1 0 001.414 0l4-4z" clipRule="evenodd" />
                        </svg>
                      </span>
                    )}
                  </div>

                  <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-sm text-gray-600 dark:text-gray-400">
                    {member.date_of_birth && !isFmFieldHidden('fm_date_of_birth') && (
                      <div>
                        <span className="font-medium">DOB:</span> {fmt.short(member.date_of_birth)}
                      </div>
                    )}
                    {member.date_of_birth && !isFmFieldHidden('fm_date_of_birth') && (
                      <div>
                        <span className="font-medium">Age:</span> {computeAge(member.date_of_birth)} yrs
                      </div>
                    )}
                    {member.phone && !isFmFieldHidden('fm_phone') && (
                      <div>
                        <span className="font-medium">Phone:</span> {member.phone}
                      </div>
                    )}
                    {member.email && !isFmFieldHidden('fm_email') && (
                      <div>
                        <span className="font-medium">Email:</span> {member.email}
                      </div>
                    )}
                    {!isFmFieldHidden('fm_coverage_type') && (
                      <div>
                        <span className="font-medium">Coverage:</span> {getCoverageLabel(member.coverage_type)}
                      </div>
                    )}
                    {customFieldDefs.map((f) => {
                      if (!isCustomFieldVisible(f, member.custom_fields as Record<string, unknown>)) return null;
                      const v = member.custom_fields?.[f.name];
                      if (v == null || v === '' || (Array.isArray(v) && v.length === 0)) return null;
                      return (
                        <div key={f.id}>
                          <span className="font-medium">{customFieldLabel(f)}:</span>{' '}
                          {Array.isArray(v) ? (v as string[]).join(', ') : String(v)}
                        </div>
                      );
                    })}
                  </div>
                </div>

                {!isReadOnly && (
                  <div className="flex gap-2 ml-4">
                    {member.verification_status !== 'VERIFIED' && (
                      <button
                        onClick={() => handleVerify(member.id)}
                        className="p-2 text-green-600 hover:text-green-700 dark:text-green-400"
                        title="Verify"
                      >
                        <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 13l4 4L19 7" />
                        </svg>
                      </button>
                    )}
                    <button
                      onClick={() => handleEdit(member)}
                      className="p-2 text-blue-600 hover:text-blue-700 dark:text-blue-400"
                      title="Edit"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M11 5H6a2 2 0 00-2 2v11a2 2 0 002 2h11a2 2 0 002-2v-5m-1.414-9.414a2 2 0 112.828 2.828L11.828 15H9v-2.828l8.586-8.586z" />
                      </svg>
                    </button>
                    <button
                      onClick={() => handleDelete(member.id)}
                      className="p-2 text-red-600 hover:text-red-700 dark:text-red-400"
                      title="Delete"
                    >
                      <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                      </svg>
                    </button>
                  </div>
                )}
              </div>

              {/* TRUS-402: repeatable field groups placed on family members.
                  Renders nothing until an admin sets a group's "Appears on" to
                  Family Members, so an org using none sees no change. */}
              <RecordFieldGroupsSection
                recordType="family_member"
                memberId={memberId}
                familyMemberId={member.id}
              />
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
